import React, { useState } from 'react';
import {
  Box,
  TextField,
  Menu,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { menuPaperSx } from './styles';

interface Option {
  label: string;
  value: string;
}

interface ResponsiveSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: (Option | string)[]; // Supports both object and string array
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

const ResponsiveSelect: React.FC<ResponsiveSelectProps> = ({
  label,
  value,
  onChange,
  options,
  required = false,
  disabled = false,
  error = false,
  helperText = '',
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    handleClose();
  };

  // Helper to normalize options
  const normalizedOptions: Option[] = options.map(opt =>
    typeof opt === 'string' ? { label: opt, value: opt } : opt
  );

  const selectedLabel = normalizedOptions.find(o => o.value === value)?.label || value;

  return (
    <Box
      sx={{
        width: '100%',
        cursor: disabled ? 'default' : 'pointer',
      }}
      onClick={handleClick}
    >
      <TextField
        fullWidth
        label={label}
        required={required}
        variant="outlined"
        value={selectedLabel}
        error={error}
        helperText={helperText}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                edge="end"
                size="small"
                sx={{ pointerEvents: 'none' }}
                disabled={disabled}
              >
                <ArrowDropDownIcon />
              </IconButton>
            </InputAdornment>
          ),
          style: { cursor: disabled ? 'default' : 'pointer' }
        }}
        sx={{
          '& .MuiInputBase-root': { cursor: disabled ? 'default' : 'pointer' },
          '& .MuiInputBase-input': { cursor: disabled ? 'default' : 'pointer' }
        }}
      />
      <Menu
        id={`menu-${label}`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'dropdown-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            ...menuPaperSx,
            width: anchorEl ? anchorEl.offsetWidth : undefined,
          },
          elevation: 3
        }}
      >
        {normalizedOptions.length > 0 ? (
          normalizedOptions.map((opt) => (
            <MenuItem
              key={opt.value}
              onClick={(e) => {
                e.stopPropagation();
                handleSelect(opt.value);
              }}
              selected={opt.value === value}
            >
              {opt.label}
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <em>No options available</em>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default ResponsiveSelect;
